import { CheckIcon, ChevronsUpDown } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ElementRef,
  type ForwardRefExoticComponent,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// Type-only imports — erased at compile time, do not pull
// `react-phone-number-input` into the static-import graph. Runtime values
// come from `usePhoneInputDeps()` / `getPhoneInputDepsSync()` so the
// package lives in a lazy chunk (ARC-386).
import type PhoneNumberInput from 'react-phone-number-input';
import type {
  Country,
  FlagProps,
  Props as RPNInputProps,
  Value,
} from 'react-phone-number-input';

import { Button } from '@heidi/design-system/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@heidi/design-system/command';
import { useIsExperimentalDesignSystemForms } from '@heidi/design-system/hooks/use-is-experimental-design-system-forms';
import { Input } from '@heidi/design-system/input';
import { Popover, PopoverContent, PopoverTrigger } from '@heidi/design-system/popover';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { type CallsAvailableCountry } from '@heidi/schema/ml-scribe';
import { cn } from '@heidi/utils';

import { getPhoneInputDepsSync, usePhoneInputDeps } from './phone-input-lazy';

const DEFAULT_ALLOWED_COUNTRIES: Country[] = ['NZ', 'AU', 'GB', 'US', 'CA', 'JP'];

type PhoneInputProps = Omit<ComponentProps<'input'>, 'onChange' | 'value' | 'ref'> &
  Omit<
    RPNInputProps<typeof PhoneNumberInput>,
    'onChange' | 'countrySelectComponent' | 'defaultCountry'
  > & {
    onChange?: (value: Value) => void;
    allowedCountries?: Country[]; // countries to show in the list
    defaultCountry?: CallsAvailableCountry | Country | null | undefined; // default country to select
    /** Applied to the country selector popover (e.g. `z-[102]` when used inside elevated modals). */
    countrySelectPopoverClassName?: string;
  };

const PhoneInput: ForwardRefExoticComponent<PhoneInputProps> = forwardRef<
  ElementRef<typeof PhoneNumberInput>,
  PhoneInputProps
>(
  (
    {
      className,
      onChange,
      value,
      allowedCountries = DEFAULT_ALLOWED_COUNTRIES,
      defaultCountry,
      countrySelectPopoverClassName,
      ...props
    },
    ref
  ) => {
    // create a wrapper so we can pass extra prop to CountrySelect
    const CountrySelectWithAllowed = useCallback(
      (selectProps: CountrySelectProps) => (
        <CountrySelect
          {...selectProps}
          allowedCountries={allowedCountries}
          popoverClassName={countrySelectPopoverClassName}
        />
      ),
      [allowedCountries, countrySelectPopoverClassName]
    );

    const previousDefaultCountryRef = useRef(defaultCountry);

    useEffect(() => {
      if (previousDefaultCountryRef.current !== defaultCountry) {
        previousDefaultCountryRef.current = defaultCountry;
        onChange?.('' as Value);
      }
    }, [defaultCountry, onChange]);

    // Render nothing until the lazy `react-phone-number-input` chunks
    // resolve. The wrapper itself stays tree-shakeable and ref-forwarding
    // intact; only the heavy UI mounts once the package is available.
    const deps = usePhoneInputDeps();
    if (!deps) return null;
    const { PhoneNumberInput: PhoneNumberInputComponent } = deps;

    return (
      <PhoneNumberInputComponent
        ref={ref}
        className={cn('flex rounded-s-none', className)}
        flagComponent={FlagComponent}
        countrySelectComponent={CountrySelectWithAllowed}
        inputComponent={InputComponent}
        smartCaret={false}
        value={value || undefined}
        defaultCountry={defaultCountry as Country}
        reset={defaultCountry}
        /**
         * Handles the onChange event.
         *
         * react-phone-number-input might trigger the onChange event as undefined
         * when a valid phone number is not entered. To prevent this,
         * the value is coerced to an empty string.
         *
         * @param {E164Number | undefined} value - The entered value
         */
        onChange={(value) => onChange?.(value || ('' as Value))}
        {...props}
      />
    );
  }
);
PhoneInput.displayName = 'PhoneInput';

const InputComponent = forwardRef<HTMLInputElement, ComponentProps<'input'>>(
  ({ className, ...props }, ref) => {
    const isExperimentalForms = useIsExperimentalDesignSystemForms();
    return (
      <Input
        className={className}
        wrapperClassName={cn(
          'w-full rounded-s-none',
          isExperimentalForms ? 'rounded-e-lg' : 'rounded-e-md'
        )}
        {...props}
        ref={ref}
      />
    );
  }
);
InputComponent.displayName = 'InputComponent';

type CountryEntry = { label: string; value: Country | undefined };

type CountrySelectProps = {
  disabled?: boolean;
  value: Country;
  options: CountryEntry[];
  onChange: (country: Country) => void;
  allowedCountries?: Country[];
  popoverClassName?: string;
};

const CountrySelect = ({
  disabled,
  value: selectedCountry,
  options: countryList,
  onChange,
  allowedCountries = DEFAULT_ALLOWED_COUNTRIES,
  popoverClassName,
}: CountrySelectProps) => {
  const intl = useIntl();
  const isExperimentalForms = useIsExperimentalDesignSystemForms();

  const orderedList = useMemo(() => {
    // Create country list directly from allowed countries for better performance
    const countryMap = new Map(countryList.map((c) => [c.value, c]));

    return allowedCountries
      .map((countryCode) => countryMap.get(countryCode))
      .filter((country): country is CountryEntry => country !== undefined);
  }, [countryList, allowedCountries]);

  const localizedCountryNameByCode = useMemo(() => {
    const names = new Map<Country, string>();
    for (const { value, label } of orderedList) {
      if (!value) continue;
      names.set(value, intl.formatDisplayName(value, { type: 'region' }) ?? label);
    }
    return names;
  }, [intl, orderedList]);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          // The experimental input is 36px (the Button's `md`); the stable one
          // is 40px. Flag-conditional so the trigger always matches the input
          // beside it (same trade as the Combobox trigger, DS-1499).
          size={isExperimentalForms ? 'md' : 'lg'}
          disableAutomaticSvgSizing
          type="button"
          variant="outline"
          className={cn(
            'flex gap-1 rounded-e-none border-e-0 px-3 focus:z-10',
            // Outer corner matches the input wrapper's flag-conditional radius;
            // the seam corner stays square against it.
            isExperimentalForms ? 'rounded-s-lg' : 'rounded-s-md'
          )}
          disabled={disabled}
        >
          <FlagComponent country={selectedCountry} countryName={selectedCountry} />
          <ChevronsUpDown
            className={cn('-me-2 size-4 opacity-50', disabled ? 'hidden' : 'opacity-100')}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn('w-[300px] overflow-hidden p-0', popoverClassName)}>
        <Command>
          <CommandInput
            value={searchValue}
            onValueChange={(value) => {
              setSearchValue(value);
              setTimeout(() => {
                if (scrollAreaRef.current) {
                  const viewportElement = scrollAreaRef.current.querySelector(
                    '[data-radix-scroll-area-viewport]'
                  );
                  if (viewportElement) {
                    viewportElement.scrollTop = 0;
                  }
                }
              }, 0);
            }}
            placeholder={intl.formatMessage({
              defaultMessage: 'Search country',
              id: 'Y4k8wKJnds',
              description:
                'Placeholder for filtering countries in the phone number input',
            })}
          />
          <CommandList>
            <ScrollArea ref={scrollAreaRef} className="h-72">
              <CommandEmpty>
                <FormattedMessage
                  defaultMessage="No country found."
                  id="L8lRpjN3GP"
                  description="Empty state when no country matches the phone input search"
                />
              </CommandEmpty>
              <CommandGroup>
                {orderedList.map(({ value, label }) => {
                  if (!value) return null;

                  const localizedCountryName =
                    localizedCountryNameByCode.get(value) ?? label;

                  return (
                    <CountrySelectOption
                      key={value}
                      country={value}
                      countryName={localizedCountryName}
                      selectedCountry={selectedCountry}
                      onChange={onChange}
                      onSelectComplete={() => setIsOpen(false)}
                    />
                  );
                })}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface CountrySelectOptionProps extends FlagProps {
  selectedCountry: Country;
  onChange: (country: Country) => void;
  onSelectComplete: () => void;
}

const CountrySelectOption = ({
  country,
  countryName,
  selectedCountry,
  onChange,
  onSelectComplete,
}: CountrySelectOptionProps) => {
  const handleSelect = useCallback(() => {
    onChange(country);
    onSelectComplete();
  }, [country, onChange, onSelectComplete]);

  // Always non-null in practice — this option only mounts inside the
  // `<PhoneNumberInput>` country-select popover, which itself only renders
  // after `usePhoneInputDeps()` resolves at the top level.
  const getCountryCallingCode = getPhoneInputDepsSync()?.getCountryCallingCode;

  return (
    <CommandItem className="gap-2" onSelect={handleSelect}>
      <FlagComponent country={country} countryName={countryName} />
      <span className="flex-1 text-sm">{countryName}</span>
      <span className="text-sm text-text-tertiary">
        {getCountryCallingCode ? `+${getCountryCallingCode(country)}` : ''}
      </span>
      <CheckIcon
        className={`ms-auto size-4 ${country === selectedCountry ? 'opacity-100' : 'opacity-0'}`}
      />
    </CommandItem>
  );
};

const FlagComponent = ({ country, countryName }: FlagProps) => {
  // Always non-null in practice — `flagComponent` is only invoked inside
  // `<PhoneNumberInput>`'s render tree, which only mounts after
  // `usePhoneInputDeps()` resolves at the top level.
  const flags = getPhoneInputDepsSync()?.flags;
  const Flag = flags?.[country];

  return (
    <span className="flex h-4 w-6 shrink-0 items-center justify-center overflow-hidden rounded-xs bg-background-primary/20 [&_svg]:min-h-full [&_svg]:min-w-full">
      {Flag && <Flag title={countryName} />}
    </span>
  );
};

export { PhoneInput };
